import { type Country } from 'react-phone-number-input';

const DEFAULT_LOCALE = 'en';

const LOCALIZED_TITLE_COUNTRY_CODES = new Set<Country>(['AR', 'TW', 'DE', 'CZ']);

const TITLE_COUNTRY_NAME_OVERRIDES: Record<string, Partial<Record<Country, string>>> = {
  en: { CZ: 'the Czech Republic' },
};

const normalizeLocale = (locale?: string): string => {
  if (!locale) return DEFAULT_LOCALE;

  const [language, ...subtags] = locale.replaceAll('_', '-').split('-');
  if (!language) return DEFAULT_LOCALE;

  const script = /^[a-z]{4}$/i.test(subtags[0] ?? '') ? subtags.shift() : undefined;
  const region = /^([a-z]{2}|\d{3})$/i.test(subtags[0] ?? '') ? subtags[0] : undefined;

  return [
    language.toLowerCase(),
    script && `${script[0].toUpperCase()}${script.slice(1).toLowerCase()}`,
    region?.toUpperCase(),
  ]
    .filter(Boolean)
    .join('-');
};

const uniq = (values: string[]): string[] =>
  values.filter((value, index, array) => Boolean(value) && array.indexOf(value) === index);

const getOverriddenCountryName = (
  countryCode: Country,
  normalizedLocale: string,
): string | null => {
  const localeKeys = uniq([normalizedLocale, normalizedLocale.split('-')[0]]);

  for (const key of localeKeys) {
    const override = TITLE_COUNTRY_NAME_OVERRIDES[key]?.[countryCode];
    if (override) return override;
  }

  return null;
};

const getCountryDisplayName = (countryCode: Country, normalizedLocale: string): string | null => {
  if (typeof Intl === 'undefined' || typeof Intl.DisplayNames === 'undefined') {
    return null;
  }

  const locales = uniq([normalizedLocale, normalizedLocale.split('-')[0], DEFAULT_LOCALE]);

  for (const currentLocale of locales) {
    try {
      const formatter = new Intl.DisplayNames([currentLocale], { type: 'region' });
      const localizedName = formatter.of(countryCode);

      if (!localizedName || localizedName === countryCode) continue;

      const resolvedLocale = normalizeLocale(formatter.resolvedOptions().locale);

      return getOverriddenCountryName(countryCode, resolvedLocale) ?? localizedName;
    } catch {
      continue;
    }
  }

  return null;
};

export const getLocalizedTitleCountryName = (
  countryCode: Country | null | undefined,
  locale: string,
): string | null => {
  if (!countryCode) {
    return null;
  }

  const normalizedCountryCode = countryCode.toUpperCase() as Country;
  if (!LOCALIZED_TITLE_COUNTRY_CODES.has(normalizedCountryCode)) {
    return null;
  }

  const normalizedLocale = normalizeLocale(locale);

  return (
    getCountryDisplayName(normalizedCountryCode, normalizedLocale) ??
    getOverriddenCountryName(normalizedCountryCode, normalizedLocale)
  );
};
